import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Lottie from 'lottie-react';
import { useSearchParams } from 'react-router-dom';
import '../csscomponent/Career.css';
import { createJobApplication, isJobFormBackendReady, uploadResumeFile } from '../lib/firebaseJobs';

const openings = [
  {
    jobId: 'SD-REACT-001',
    title: 'Frontend Developer (React)',
    type: 'Full Time',
    location: 'Remote / Pune',
    detail: 'Build modern UI systems, accessible interfaces, and conversion-focused web pages.',
    shortJD:
      'Build responsive React interfaces, integrate APIs, and improve page performance for production web apps.',
    fullJD:
      'Required Skills: Strong React, JavaScript (ES6+), HTML/CSS, Git, API integration, and responsive UI building. Good understanding of performance optimization and component architecture is preferred. College Eligibility: B.E/B.Tech, BCA, MCA, or equivalent degree in CS/IT (final year and fresh graduates can apply if fundamentals are strong).',
  },
  {
    jobId: 'SD-UIUX-002',
    title: 'UI/UX Designer',
    type: 'Contract',
    location: 'Remote',
    detail: 'Create high-impact visual design systems and user journeys for web products.',
    shortJD:
      'Design user journeys, wireframes, and clean visual systems that are implementation-ready for web products.',
    fullJD:
      'Required Skills: Figma, wireframing, prototyping, design systems, user research basics, and clear developer handoff. Strong typography and layout skills are expected. College Eligibility: B.Des, BFA, BSc Design, or equivalent; final year students with strong portfolios are eligible.',
  },
  {
    jobId: 'SD-BA-003',
    title: 'Business Analyst',
    type: 'Full Time',
    location: 'Hybrid',
    detail: 'Translate business goals into clear technical scope, timelines, and product plans.',
    shortJD:
      'Gather requirements, prepare BRD/PRD documents, and align business goals with product delivery plans.',
    fullJD:
      'Required Skills: Requirement gathering, documentation (BRD/PRD), communication, stakeholder management, and basic understanding of software delivery lifecycle. Excel/Sheets and presentation skills are important. College Eligibility: MBA, BBA, B.Com, B.E/B.Tech, or equivalent with strong analytical and communication skills.',
  },
];

const steps = ['Apply with your resume', 'Intro + technical discussion', 'Practical task round', 'Offer and onboarding'];
const allowedResumeExtensions = ['pdf', 'doc', 'docx'];
const validEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
const validPhone = (value) => /^[0-9+\-\s()]{10,15}$/.test(value.trim());

const initialForm = {
  fullName: '',
  email: '',
  phone: '',
  role: openings[0].title,
  location: '',
  skills: '',
  candidateType: 'fresher',
  yearsExperience: '',
  previousCompany: '',
  lastWorkingDate: '',
  coverLetter: '',
};

function JobApply() {
  const [searchParams] = useSearchParams();
  const [careerAnim, setCareerAnim] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeError, setResumeError] = useState('');
  const [status, setStatus] = useState({ type: '', text: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentView, setCurrentView] = useState('list');
  const [selectedJob, setSelectedJob] = useState(null);
  const [expandedJD, setExpandedJD] = useState('');

  useEffect(() => {
    let mounted = true;
    import('../jobhunting.json').then((mod) => {
      if (mounted) setCareerAnim(mod.default);
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const queryRole = searchParams.get('role');
    if (!queryRole) return;
    const matchedRole = openings.find((item) => item.title === queryRole);
    if (matchedRole) {
      setForm((prev) => ({ ...prev, role: matchedRole.title }));
      setSelectedJob(matchedRole);
      setCurrentView('details');
      setTimeout(() => {
        document.getElementById('job-details-screen')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    }
  }, [searchParams]);

  const isExperienced = form.candidateType === 'experienced';

  const isFormValid = useMemo(() => {
    if (!validEmail(form.email) || !validPhone(form.phone)) return false;
    if (!form.fullName.trim() || !form.role.trim() || !form.location.trim() || !form.skills.trim()) return false;
    if (!resumeFile || resumeError) return false;
    if (isExperienced) {
      if (!form.yearsExperience || Number(form.yearsExperience) < 1) return false;
      if (!form.previousCompany.trim() || !form.lastWorkingDate) return false;
    }
    return true;
  }, [form, isExperienced, resumeError, resumeFile]);

  const handleInput = (event) => {
    const { name, value } = event.target;
    setStatus({ type: '', text: '' });

    if (name === 'candidateType') {
      setForm((prev) => ({
        ...prev,
        candidateType: value,
        yearsExperience: value === 'experienced' ? prev.yearsExperience : '',
        previousCompany: value === 'experienced' ? prev.previousCompany : '',
        lastWorkingDate: value === 'experienced' ? prev.lastWorkingDate : '',
      }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoleApply = (role) => {
    setSelectedJob(role);
    setForm((prev) => ({ ...prev, role: role.title }));
    setCurrentView('details');
    document.getElementById('job-details-screen')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCloseForm = () => {
    setCurrentView('list');
    setStatus({ type: '', text: '' });
  };

  const handleProceedToForm = () => {
    if (!selectedJob) return;
    setForm((prev) => ({ ...prev, role: selectedJob.title }));
    setCurrentView('form');
    setTimeout(() => {
      document.getElementById('job-application-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 40);
  };

  const handleResumeChange = (event) => {
    const file = event.target.files?.[0];
    setStatus({ type: '', text: '' });
    setResumeError('');

    if (!file) {
      setResumeFile(null);
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    const isAllowed = allowedResumeExtensions.includes(extension);
    const maxSize = 5 * 1024 * 1024;

    if (!isAllowed) {
      setResumeFile(null);
      setResumeError('Upload PDF, DOC, or DOCX only.');
      return;
    }
    if (file.size > maxSize) {
      setResumeFile(null);
      setResumeError('Resume size must be less than 5MB.');
      return;
    }

    setResumeFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: '', text: '' });

    if (!isFormValid) {
      setStatus({ type: 'error', text: 'Please fill all required fields correctly before applying.' });
      return;
    }

    if (!isJobFormBackendReady) {
      setStatus({
        type: 'error',
        text: 'Application backend is not configured. Please add Firebase env values and restart.',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const resume = await uploadResumeFile(resumeFile, {
        role: form.role,
        candidateName: form.fullName,
      });

      const id = await createJobApplication({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        role: form.role.trim(),
        location: form.location.trim(),
        skills: form.skills
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        candidateType: form.candidateType,
        yearsExperience: isExperienced ? Number(form.yearsExperience) : 0,
        previousCompany: isExperienced ? form.previousCompany.trim() : '',
        lastWorkingDate: isExperienced ? form.lastWorkingDate : '',
        coverLetter: form.coverLetter.trim(),
        resume,
      });

      setStatus({
        type: 'success',
        text: `Application submitted successfully. Reference ID: ${id}`,
      });
      setForm((prev) => ({
        ...initialForm,
        role: prev.role,
      }));
      setResumeFile(null);
    } catch (error) {
      setStatus({
        type: 'error',
        text: `Submission failed. ${error.message || 'Please try again.'}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="career-page">
      <motion.div
        className="career-intro"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div>
          <p>Job Apply</p>
          <h1>Apply with complete details. We review every profile seriously.</h1>
          <span>Choose a role, upload your resume, and submit a structured application in one place.</span>
        </div>
        <div className="career-lottie-wrap">
          {careerAnim ? <Lottie animationData={careerAnim} loop className="career-lottie" /> : null}
        </div>
      </motion.div>

      {currentView === 'list' ? <div className="career-grid">
        {openings.map((role, index) => (
          <motion.article
            key={role.title}
            className="career-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
          >
            <h3>{role.title}</h3>
            <div className="career-meta">
              <span>{role.type}</span>
              <span>{role.location}</span>
            </div>
            <p>{role.detail}</p>
            <p className="career-jd-short">{role.shortJD}</p>
            <button
              type="button"
              className="career-jd-btn"
              onClick={() => setExpandedJD((prev) => (prev === role.title ? '' : role.title))}
            >
              {expandedJD === role.title ? 'View Less' : 'View More'}
            </button>
            {expandedJD === role.title ? <p className="career-jd-full">{role.fullJD}</p> : null}
            <button type="button" className="career-apply-btn" onClick={() => handleRoleApply(role)}>
              Apply for this role
            </button>
          </motion.article>
        ))}
      </div> : null}

      {currentView === 'details' && selectedJob ? (
        <motion.section
          id="job-details-screen"
          className="job-details-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="job-form-head">
            <h2>{selectedJob.title}</h2>
            <button type="button" className="job-form-close" onClick={handleCloseForm}>
              Close
            </button>
          </div>
          <p className="job-id-chip">Job ID: {selectedJob.jobId}</p>
          <p className="job-details-copy">{selectedJob.detail}</p>
          <p className="job-details-copy">{selectedJob.fullJD}</p>
          <div className="job-details-actions">
            <button type="button" className="career-apply-btn" onClick={handleProceedToForm}>
              Continue to Apply
            </button>
          </div>
        </motion.section>
      ) : null}

      {currentView === 'form' ? (
        <motion.form
          id="job-application-form"
          className="job-apply-form"
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="job-form-head">
            <h2>Application Form</h2>
            <button type="button" className="job-form-close" onClick={handleCloseForm}>
              Close
            </button>
          </div>
          <p className="job-selected-role">
            Applying for: {form.role}
            {selectedJob ? ` | Job ID: ${selectedJob.jobId}` : ''}
          </p>
          <div className="job-form-grid">
          <label>
            Full Name
            <input name="fullName" value={form.fullName} onChange={handleInput} placeholder="Your full name" required />
          </label>

          <label>
            Email
            <input name="email" type="email" value={form.email} onChange={handleInput} placeholder="you@example.com" required />
          </label>

          <label>
            Phone Number
            <input name="phone" value={form.phone} onChange={handleInput} placeholder="+91 9876543210" required />
          </label>

          <label>
            Applying For
            <select name="role" value={form.role} onChange={handleInput} required>
              {openings.map((role) => (
                <option key={role.title} value={role.title}>
                  {role.title}
                </option>
              ))}
            </select>
          </label>

          <label>
            Current Location
            <input name="location" value={form.location} onChange={handleInput} placeholder="City, State" required />
          </label>

          <label>
            Skills (comma separated)
            <input
              name="skills"
              value={form.skills}
              onChange={handleInput}
              placeholder="React, JavaScript, Figma"
              required
            />
          </label>

          <label>
            Candidate Type
            <select name="candidateType" value={form.candidateType} onChange={handleInput}>
              <option value="fresher">Fresher</option>
              <option value="experienced">Experienced</option>
            </select>
          </label>

          {isExperienced ? (
            <>
              <label>
                Years of Experience
                <input
                  name="yearsExperience"
                  type="number"
                  min="1"
                  step="1"
                  value={form.yearsExperience}
                  onChange={handleInput}
                  required={isExperienced}
                />
              </label>

              <label>
                Previous Company Name
                <input
                  name="previousCompany"
                  value={form.previousCompany}
                  onChange={handleInput}
                  placeholder="Last company name"
                  required={isExperienced}
                />
              </label>

              <label>
                Last Working Date
                <input
                  name="lastWorkingDate"
                  type="date"
                  value={form.lastWorkingDate}
                  onChange={handleInput}
                  required={isExperienced}
                />
              </label>
            </>
          ) : null}

          <label className="job-form-full">
            Cover Letter (optional)
            <textarea
              name="coverLetter"
              rows="4"
              value={form.coverLetter}
              onChange={handleInput}
              placeholder="Briefly describe your fit for this role"
            />
          </label>

          <label className="job-form-full">
            Upload Resume (PDF, DOC, DOCX - max 5MB)
            <input type="file" accept=".pdf,.doc,.docx" onChange={handleResumeChange} required />
            {resumeFile ? <small className="job-file-ok">Selected: {resumeFile.name}</small> : null}
            {resumeError ? <small className="job-file-error">{resumeError}</small> : null}
          </label>
          </div>

          <button type="submit" className="job-submit-btn" disabled={!isFormValid || isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Apply Now'}
          </button>

          {status.text ? <p className={`job-form-status ${status.type}`}>{status.text}</p> : null}
        </motion.form>
      ) : null}

      <div className="career-flow">
        <h2>Our Hiring Process</h2>
        <div className="flow-list">
          {steps.map((step, index) => (
            <div key={step} className="flow-item">
              <strong>{index + 1}</strong>
              <span>{step}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default JobApply;
